import { ApiHttpError } from "@lafayette/api-client";
import type {
  AnalyticsRange,
  AnalyticsTab,
  StaffAnalyticsCustomerPage,
  StaffAnalyticsOpening,
  StaffAnalyticsOverview,
} from "@lafayette/schemas";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { useAnalyticsRuntime } from "./runtime";

// The reads of the Analytics area. The answers hold a customer name and money, so nothing stays in the cache after the page lets go
// (`gcTime: 0`) and nothing is written to browser storage. A failed read is not retried by the library: the page says so and offers a retry.
//
// THE OPENING (ADR 0017, decision 10). Each time a tab view starts (the tab mounts, or the customer or the tab changes) the page asks the API for
// ONE audited opening. Every read of that view names it. A range change is NOT an opening. A read that the API refuses makes the page open ONCE
// more and try again; a second refusal is shown as it is (the same words for every cause: the page never tells them apart).

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  // The app's query client keeps the PREVIOUS key's data as a placeholder. Here that would show the opening of the old tab as the opening of the
  // new one for a moment: a read would use it (or find none) and be refused, which writes a false denial row. A new key shows its own loading state.
  placeholderData: () => undefined,
} as const;

const analyticsKeys = {
  customers: (query: string) => ["analytics-customers", query] as const,
  opening: (customerId: string, tab: AnalyticsTab) =>
    ["analytics-opening", customerId, tab] as const,
  overview: (customerId: string, range: AnalyticsRange, tz: string) =>
    ["analytics-overview", customerId, range, tz] as const,
};

/** A refusal of the API: `403`, whatever the cause. */
export function isRefusal(error: unknown): boolean {
  return error instanceof ApiHttpError && error.status === 403;
}

/** A request that the API found malformed (`400`): for example a time window outside the range. */
export function isBadRequest(error: unknown): boolean {
  return error instanceof ApiHttpError && error.status === 400;
}

const PICKER_PAGE = 20;

/** The customers the staff member may open, filtered by the server. `query` is already trimmed; an empty one lists all. */
export function useAnalyticsCustomers(query: string, enabled: boolean) {
  const { api } = useAnalyticsRuntime();
  return useInfiniteQuery<StaffAnalyticsCustomerPage>({
    queryKey: analyticsKeys.customers(query),
    queryFn: ({ pageParam, signal }) =>
      api.customers(
        {
          limit: PICKER_PAGE,
          ...(query === "" ? {} : { query }),
          ...(typeof pageParam === "string" ? { cursor: pageParam } : {}),
        },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    ...READ_OPTIONS,
  });
}

export type CustomerView = Readonly<{
  /** The first opening of this view: the access object (the name, the reason, the consent) for the header, before any read has answered. */
  opening: ReturnType<typeof useOpeningQuery>;
  /**
   * `true` when the opening of THIS customer and THIS tab has answered. The `opening` query alone is not enough: for a moment after the tab or the
   * customer changes it still shows the answer of the view before, and a read that started then would name an opening of another tab.
   */
  ready: boolean;
  /** Runs a read inside the opening: it opens again once and retries once when the API refuses the read. */
  read: <T>(call: (openingId: string) => Promise<T>) => Promise<T>;
}>;

function useOpeningQuery(
  customerId: string,
  tab: AnalyticsTab,
  current: { current: string | null },
  openedFor: { current: string | null },
) {
  const { api } = useAnalyticsRuntime();
  // A new view has no opening until its own one answers: a read of the old view must not use the old id.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the customer and the tab are the signal; the effect only drops the old id.
  useEffect(() => {
    current.current = null;
    openedFor.current = null;
  }, [customerId, tab, current, openedFor]);
  return useQuery<StaffAnalyticsOpening>({
    queryKey: analyticsKeys.opening(customerId, tab),
    queryFn: async ({ signal }) => {
      const opening = await api.open(customerId, tab, { signal });
      current.current = opening.openingId;
      openedFor.current = `${customerId}|${tab}`;
      return opening;
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    ...READ_OPTIONS,
  });
}

/** One audited view of one customer and one tab. */
export function useCustomerView(customerId: string, tab: AnalyticsTab): CustomerView {
  const { api } = useAnalyticsRuntime();
  const current = useRef<string | null>(null);
  // Several reads of one view run at once (the Overview and the recent visits). When the opening is refused for all of them, ONE new opening is
  // made and the others use it: a view does not write an audit row for each of its reads.
  const reopening = useRef<Promise<string> | null>(null);
  const openedFor = useRef<string | null>(null);
  const opening = useOpeningQuery(customerId, tab, current, openedFor);
  const read = useCallback(
    async <T>(call: (openingId: string) => Promise<T>): Promise<T> => {
      const given = current.current;
      if (given === null) throw new Error("the view has no opening yet");
      try {
        return await call(given);
      } catch (error) {
        if (!isRefusal(error)) throw error;
        // Another read of this view has already opened again: use that opening.
        if (current.current !== null && current.current !== given) return call(current.current);
        reopening.current ??= api
          .open(customerId, tab)
          .then((fresh) => {
            current.current = fresh.openingId;
            return fresh.openingId;
          })
          .finally(() => {
            reopening.current = null;
          });
        return call(await reopening.current);
      }
    },
    [api, customerId, tab],
  );
  return {
    opening,
    ready: opening.isSuccess && openedFor.current === `${customerId}|${tab}`,
    read,
  };
}

/**
 * The Overview of one customer in one range. It waits for the opening of the view, and it is read ONLY on the Overview tab: the opening of
 * another tab is not valid for this read, so a read there would be refused and would write a false denial row.
 */
export function useCustomerOverview(
  view: CustomerView,
  customerId: string,
  range: AnalyticsRange,
  tab: AnalyticsTab,
) {
  const { api, timeZone } = useAnalyticsRuntime();
  const tz = timeZone();
  return useQuery<StaffAnalyticsOverview>({
    queryKey: analyticsKeys.overview(customerId, range, tz),
    queryFn: ({ signal }) =>
      view.read((openingId) => api.overview(customerId, openingId, { range, tz }, { signal })),
    enabled: tab === "overview" && view.ready,
    ...READ_OPTIONS,
  });
}
